import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, ShieldAlert, Sun, Copy, Check } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { useSite } from "../lib/site";
import { useT, LangSwitch } from "../lib/i18n";

const NAV = [
  ["/", "home"], ["/become-a-solar-partner", "programme"], ["/how-it-works", "how"], ["/solar-solutions", "solutions"],
  ["/resources", "resources"], ["/about", "about"], ["/contact", "contact"],
];

export function Logo({ compact }) {
  const { brand_name, primary_brand } = useSite();
  const { tl } = useT();
  return (
    <Link to="/" className="flex items-center gap-2 font-heading font-bold text-slate-900" data-testid="header-logo">
      {primary_brand === "toshniwal"
        ? <img src="/brand/toshniwal-brothers-logo-320.webp" alt={tl("Toshniwal Brothers logo")} width="40" height="40" className="h-10 w-10 rounded-md border object-contain" />
        : <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#0B1B3D] text-amber-400"><Sun className="h-5 w-5" /></span>}
      <span className={`text-base sm:text-lg whitespace-nowrap ${compact ? "hidden sm:inline" : ""}`}>{brand_name}</span>
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  const link = ({ isActive }) => `text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-amber-800" : "text-slate-700 hover:text-slate-950"}`;
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-amber-900/10">
      <div className="container-x flex items-center justify-between gap-6 h-16">
        <Logo />
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5" aria-label={t("nav.main")}>
          {NAV.map(([to, l]) => <NavLink key={to} to={to} end className={link} data-testid={`nav-${to.slice(1) || "home"}`}>{t(`nav.${l}`)}</NavLink>)}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <LangSwitch />
          <Link to="/sign-in" className="text-sm font-medium text-slate-700 hover:text-slate-950" data-testid="nav-partner-login">{t("nav.login")}</Link>
          <Link to="/apply" className="bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap" data-testid="nav-apply-btn">{t("nav.apply")}</Link>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger className="xl:hidden p-2" aria-label={t("nav.openMenu")} data-testid="mobile-menu-btn"><Menu /></SheetTrigger>
          <SheetContent side="right" className="w-72">
            <SheetTitle className="font-heading">{t("nav.menu")}</SheetTitle>
            <LangSwitch className="mt-4" />
            <nav className="mt-6 flex flex-col gap-4" onClick={() => setOpen(false)}>
              {NAV.map(([to, l]) => <NavLink key={to} to={to} end className={link} data-testid={`mnav-${to.slice(1) || "home"}`}>{t(`nav.${l}`)}</NavLink>)}
              <NavLink to="/sign-in" className={link} data-testid="mnav-login">{t("nav.login")}</NavLink>
              <Link to="/apply" className="bg-amber-700 text-white text-center font-semibold px-4 py-2 rounded-lg" data-testid="mnav-apply">{t("nav.apply")}</Link>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

const FOOT = [
  ["footProgramme", [["/become-a-solar-partner", "Become a partner"], ["/partner-plans", "Partner plans"], ["/how-it-works", "How it works"], ["/lead-allocation-policy", "Lead allocation policy"], ["/commission-payout-policy", "Commission & payout policy"], ["/training-academy", "Training academy"], ["/faq", "FAQ"]]],
  ["footCustomers", [["/solar-solutions", "Solar solutions"], ["/solar-solutions/residential-rooftop-solar", "Residential rooftop"], ["/solar-solutions/commercial-industrial-solar", "Commercial & industrial"], ["/solar-enquiry", "Solar enquiry"], ["/solar-partner-rajasthan", "Rajasthan"], ["/blog", "Knowledge centre"], ["/resources", "Resources"]]],
  ["footLegal", [["/contact", "Contact"], ["/grievance", "Grievance & complaints"], ["/privacy-policy", "Privacy policy"], ["/terms", "Terms"], ["/partner-agreement", "Partner agreement"], ["/cancellation-refund-policy", "Cancellation & refund"], ["/cookie-policy", "Cookies"], ["/brand-usage-policy", "Brand usage"], ["/sitemap", "Sitemap"]]],
];

export const telHref = (p) => `tel:${p.replace(/[^\d+]/g, "")}`;

function CopyBtn({ value, testid }) {
  const { tl } = useT();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { setDone(false); }
  };
  return (
    <button type="button" onClick={copy} aria-label={`${tl("Copy")} ${value}`} title={tl(done ? "Copied" : "Copy")} className="inline-flex items-center gap-1 text-xs border rounded-md px-1.5 py-0.5 bg-white text-slate-700 hover:bg-slate-50 transition-colors" data-testid={`copy-${testid}`}>
      {done ? <Check className="h-3 w-3 text-emerald-700" /> : <Copy className="h-3 w-3" />}{tl(done ? "Copied" : "Copy")}
    </button>
  );
}

function Contactable({ href, value, testid, copy, link }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 mr-3">
      <a href={href} className={`${link} whitespace-nowrap`} data-testid={testid}>{value}</a>
      {copy && <CopyBtn value={value} testid={testid} />}
    </span>
  );
}

export function OperatorBlock({ dark, copy }) {
  const { operator } = useSite();
  const { t, tl } = useT();
  const cls = dark ? "text-slate-400" : "text-slate-600";
  const link = dark ? "text-slate-200 underline hover:text-white" : "text-amber-800 underline hover:text-amber-900";
  if (!operator) return <p className={`text-sm ${cls}`} data-testid="operator-pending">{t("common.operatorPending")}</p>;
  const label = (l) => <span className={`font-medium ${dark ? "text-slate-300" : "text-slate-800"}`}>{tl(l)}: </span>;
  const phones = [operator.phone, operator.phone2].filter(Boolean);
  const emails = [[operator.email, "Primary email"], [operator.email2, "Secondary email"]].filter(([e]) => e);
  return (
    <div className={`text-sm space-y-2 break-words ${cls}`} data-testid="operator-details">
      <p className={`font-semibold ${dark ? "text-slate-200" : "text-slate-900"}`} data-testid="operator-name">{operator.public_name || operator.legal_name}{operator.trade_name && ` (${t("common.tradingAs")} ${operator.trade_name})`}</p>
      {[["contact_person", "Contact person"], ["registered_address", "Registered address"], ["office_address", "Office address"]].map(([k, l]) => operator[k] && <p key={k} data-testid={`operator-${k}`}>{label(l)}{operator[k]}</p>)}
      {phones.length > 0 && <div className="flex flex-wrap items-baseline gap-x-1 gap-y-1" data-testid="operator-phones">{label("Phone")}<span className="flex flex-col gap-1">{phones.map((p, i) => <Contactable key={p} href={telHref(p)} value={p} testid={`operator-phone-${i}`} copy={copy} link={link} />)}</span></div>}
      {emails.map(([e, l], i) => <p key={e} data-testid={`operator-email-row-${i}`}>{label(l)}<Contactable href={`mailto:${e}`} value={e} testid={`operator-email-${i}`} copy={copy} link={link} /></p>)}
      {operator.gstin && <p data-testid="operator-gstin">{label("GSTIN")}<span className="font-mono">{operator.gstin}</span></p>}
      {operator.grievance_contact && <p data-testid="operator-grievance">{label("Grievance contact")}{operator.grievance_contact}</p>}
    </div>
  );
}

function Footer() {
  const { brand_name, apn_brand_text } = useSite();
  const { t } = useT();
  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 pb-10" data-testid="site-footer">
      <div className="container-x grid gap-10 md:grid-cols-4">
        <div className="space-y-4">
          <p className="font-heading text-white text-lg font-bold">{brand_name}</p>
          <OperatorBlock dark />
          {apn_brand_text && <p className="text-xs text-slate-400">{apn_brand_text}</p>}
        </div>
        {FOOT.map(([h, links]) => (
          <div key={h}>
            <p className="font-heading text-white font-semibold mb-3">{t(`common.${h}`)}</p>
            <ul className="space-y-2">{links.map(([to, l]) => <li key={to}><Link to={to} className="text-sm hover:text-white">{t(`paths.${to}`, null, l)}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="container-x mt-12 pt-6 border-t border-slate-800 text-xs text-slate-400 space-y-2">
        <p>{t("common.noGuarantee")} {t("common.footerNote")}</p>
        <p>© {new Date().getFullYear()} {brand_name}</p>
      </div>
    </footer>
  );
}

export function Disclaimer({ children, testid = "disclaimer-banner" }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg text-sm text-amber-950 flex items-start gap-3" data-testid={testid} role="note">
      <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" /><div>{children}</div>
    </div>
  );
}

export function Breadcrumbs({ items }) {
  const { t } = useT();
  return (
    <nav aria-label={t("common.breadcrumb")} className="text-sm text-slate-600 mb-6" data-testid="breadcrumbs">
      <ol className="flex flex-wrap gap-1">
        <li><Link to="/" className="hover:text-slate-900">{t("common.home")}</Link></li>
        {items.map((c, i) => (
          <li key={c.to} className="flex gap-1"><span aria-hidden>/</span>
            {i === items.length - 1 ? <span aria-current="page" className="text-slate-900">{t(`paths.${c.to}`, null, c.name)}</span> : <Link to={c.to} className="hover:text-slate-900">{t(`paths.${c.to}`, null, c.name)}</Link>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function Layout() {
  const { t } = useT();
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:p-2 focus:bg-white">{t("nav.skip")}</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
